import { useEffect, useState } from "react";
import { Avatar, type AvatarProps } from "@mantine/core";
import { useQuery, type QueryFunctionContext } from "@tanstack/react-query";

export type PhotoSource = {
	key: string;
	load: (signal: AbortSignal) => Promise<Blob | null>;
};
const missingIds = new Set(["", "0", "none", "null", "undefined"]);
export function isPhotoIdentity(value: string | null | undefined) {
	return !missingIds.has((value ?? "").trim().toLowerCase());
}

export function signedPhotoSource(
	url: string | null | undefined,
	refresh?: () => Promise<string | undefined>,
	size: 0 | 96 | 256 = 96,
): PhotoSource | undefined {
	if (!url) return;
	const parsed = new URL(url, window.location.origin);
	const match = /\/personas\/(staff|student)\/([^/]+)\/photo$/.exec(
		parsed.pathname,
	);
	if (!match || !isPhotoIdentity(decodeURIComponent(match[2]))) return;
	if (size) parsed.searchParams.set("size", String(size));
	const version = parsed.searchParams.get("v") ?? "";
	return {
		// A refreshed signature still describes the same image. Include origin,
		// identity, version and resolution, but never keep the token in the key.
		key: `${parsed.origin}${parsed.pathname}?v=${version}&size=${size}`,
		load: async (signal) => {
			let response = await fetch(parsed.href, { signal, credentials: "omit" });
			if ((response.status === 401 || response.status === 403) && refresh) {
				const replacement = await refresh();
				if (replacement) {
					const renewed = new URL(replacement, window.location.origin);
					if (size) renewed.searchParams.set("size", String(size));
					response = await fetch(renewed.href, { signal, credentials: "omit" });
				}
			}
			if (response.status === 404 || response.status === 204) return null;
			if (!response.ok) throw new Error("Photo unavailable");
			return response.blob();
		},
	};
}

export function photoQueryOptions(source: PhotoSource | undefined) {
	return {
		queryKey: ["shared-user-photo", source?.key ?? "missing"],
		queryFn: ({ signal }: QueryFunctionContext) =>
			source ? source.load(signal) : Promise.resolve(null),
		staleTime: (query: { state: { data?: Blob | null } }) =>
			query.state.data === null ? 2 * 60_000 : 10 * 60_000,
		gcTime: 5 * 60_000,
		retry: false,
		retryOnMount: false,
		refetchOnWindowFocus: false,
	};
}

export function usePhoto(source: PhotoSource | undefined, visible = true) {
	const query = useQuery({
		...photoQueryOptions(source),
		enabled: !!source && visible,
	});
	const [image, setImage] = useState<{ blob: Blob; url: string }>();
	useEffect(() => {
		const blob = query.data;
		if (!blob || !visible) {
			setImage(undefined);
			return;
		}
		// Cached photos are available as soon as a virtual row mounts. Updating
		// React state here can feed another passive-effect update into the same
		// scrolling commit. Publish the URL on the next frame instead, and skip
		// allocation entirely if the row disappears before that frame.
		let url: string | undefined;
		const frame = window.requestAnimationFrame(() => {
			url = URL.createObjectURL(blob);
			setImage({ blob, url });
		});
		return () => {
			window.cancelAnimationFrame(frame);
			if (url) URL.revokeObjectURL(url);
		};
	}, [query.data, visible]);
	return visible && image?.blob === query.data ? image?.url : undefined;
}

export function PhotoAvatar({
	source,
	eager = false,
	component = "div",
	...props
}: Omit<AvatarProps, "src"> & {
	source?: PhotoSource;
	eager?: boolean;
	component?: "div" | "button";
}) {
	const [node, setNode] = useState<HTMLDivElement | HTMLButtonElement | null>(
		null,
	);
	const [visible, setVisible] = useState(eager);
	useEffect(() => {
		if (!node || visible) return;
		if (!window.IntersectionObserver) {
			setVisible(true);
			return;
		}
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					setVisible(true);
					observer.disconnect();
				}
			},
			{ rootMargin: "150px" },
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, [node, visible]);
	const src = usePhoto(source, visible);
	return (
		<Avatar
			{...props}
			component={component}
			ref={setNode}
			src={src}
			imageProps={{ decoding: "async", ...props.imageProps }}
		/>
	);
}
